import { useState, useEffect, useCallback, useRef } from "react";
import Editor from "@monaco-editor/react";
import { api } from "../api/client";

const TABS = ["Organisation", "E-Mail / SMTP", "PDF Design", "Sitzungen", "Protokoll", "System", "Sitzungsorte", "Standard-TOPs", "Info-Screen"];

function FormGroup({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#999", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}


function StandardTopsTab({ api }) {
  const [gremien, setGremien] = useState([]);
  const [selected, setSelected] = useState(0);
  const [tops, setTops] = useState([]);
  const [form, setForm] = useState({ titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, optional: false });
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.getGremien().then(setGremien).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (selected) {
      api.listStandardTops(selected).then(setTops).catch(() => setTops([]));
    } else {
      setTops([]);
    }
  }, [selected, api]);

  const reload = async () => {
    if (selected) setTops(await api.listStandardTops(selected));
  };

  const save = async () => {
    if (!form.titel.trim()) { setError("Titel fehlt"); return; }
    try {
      if (editing) {
        await api.updateStandardTop(selected, editing.id, form);
      } else {
        await api.createStandardTop(selected, form);
      }
      setForm({ titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, optional: false });
      setEditing(null);
      setError("");
      reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`TOP "${t.titel}" wirklich löschen?`)) return;
    await api.deleteStandardTop(selected, t.id);
    reload();
  };

  const move = async (idx, delta) => {
    const newOrder = [...tops];
    const target = idx + delta;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    await api.reorderStandardTops(selected, newOrder.map(t => t.id));
    reload();
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      titel: t.titel, beschreibung: t.beschreibung || "",
      top_typ: t.top_typ || "standard",
      oeffentlich: t.oeffentlich !== false,
      optional: !!t.optional,
    });
  };

  return (
    <div>
      <div className="card mb-16">
        <div className="card-header"><h3>Standard-Tagesordnung pro Gremium</h3></div>
        <div className="card-body">
          <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
            Diese TOPs werden bei jeder neuen Sitzung dieses Gremiums automatisch eingefügt.
            Als „optional" markierte TOPs werden nicht automatisch kopiert.
          </p>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Gremium</label>
            <select className="form-control" value={selected}
              onChange={e => setSelected(parseInt(e.target.value, 10))}>
              <option value={0}>— Gremium wählen —</option>
              {gremien.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>

          {selected !== 0 && (
            <>
              {tops.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">📋</div><p>Noch keine Standard-TOPs definiert</p></div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Titel</th>
                      <th style={{ width: 80 }}>Typ</th>
                      <th style={{ width: 80 }}>Optional</th>
                      <th style={{ width: 160 }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tops.map((t, idx) => (
                      <tr key={t.id}>
                        <td style={{ fontFamily: "var(--mono)" }}>{t.position}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{t.titel}</div>
                          {t.beschreibung && <div style={{ fontSize: 12, color: "#666", whiteSpace: "pre-wrap" }}>{t.beschreibung}</div>}
                        </td>
                        <td style={{ fontSize: 12 }}>{t.top_typ}</td>
                        <td>{t.optional ? "✓" : "—"}</td>
                        <td style={{ display: "flex", gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => move(idx, -1)} disabled={idx === 0}>↑</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => move(idx, 1)} disabled={idx === tops.length - 1}>↓</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => startEdit(t)}>✎</button>
                          <button className="btn btn-danger btn-sm" onClick={() => remove(t)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="card mt-16" style={{ background: "#fafafa" }}>
                <div className="card-header"><h4>{editing ? `TOP #${editing.position} bearbeiten` : "Neuen Standard-TOP hinzufügen"}</h4></div>
                <div className="card-body">
                  <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
                    <strong>💡 Verfügbare Platzhalter in Titel und Beschreibung:</strong>
                    <div style={{ marginTop: 6, fontFamily: "var(--mono)", fontSize: 11, lineHeight: 1.6 }}>
                      <div><code>{"{letzte_sitzung_aktenzeichen}"}</code> – z.B. „GWV-03/26"</div>
                      <div><code>{"{letzte_sitzung_datum}"}</code> – z.B. „12.05.2026"</div>
                      <div><code>{"{letzte_sitzung_titel}"}</code> – Titel der vorherigen Sitzung</div>
                      <div><code>{"{letzte_sitzung_protokoll_status}"}</code> – „genehmigt" / „abgelehnt" / „ausstehend" …</div>
                      <div><code>{"{letzte_sitzung_genehmigung_ergebnis}"}</code> – „8 von 9 genehmigt, 0 abgelehnt, 1 ausstehend"</div>
                    </div>
                  </div>
                  {error && <div style={{ color: "var(--red)", marginBottom: 8 }}>{error}</div>}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label className="form-label">Titel *</label>
                      <input className="form-control" value={form.titel}
                        onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label">Typ</label>
                      <select className="form-control" value={form.top_typ}
                        onChange={e => setForm(f => ({ ...f, top_typ: e.target.value }))}>
                        <option value="standard">Standard</option>
                        <option value="information">Information</option>
                        <option value="beschluss">Beschluss</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <label className="form-label">Beschreibung (optional)</label>
                    <textarea className="form-control" rows={3} value={form.beschreibung}
                      onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
                      placeholder="Wird als Default-Beschreibung in die Sitzung kopiert" />
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="checkbox" checked={form.oeffentlich}
                        onChange={e => setForm(f => ({ ...f, oeffentlich: e.target.checked }))} />
                      Öffentlich
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="checkbox" checked={form.optional}
                        onChange={e => setForm(f => ({ ...f, optional: e.target.checked }))} />
                      Optional (wird nicht automatisch eingefügt)
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                    {editing && (
                      <button className="btn btn-ghost" onClick={() => {
                        setEditing(null);
                        setForm({ titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, optional: false });
                      }}>Abbrechen</button>
                    )}
                    <button className="btn btn-primary" onClick={save}>
                      {editing ? "Speichern" : "+ TOP hinzufügen"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Einstellungen() {
  const [tab, setTab] = useState(0);
  const [werte, setWerte] = useState({});
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.getEinstellungenFlat().then(setWerte).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setWerte(w => ({ ...w, [key]: val }));

  const save = async (keys) => {
    const data = {};
    keys.forEach(k => { data[k] = werte[k] || ""; });
    try {
      await api.updateEinstellungen(data);
      setSuccess("Gespeichert!");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Fehler beim Speichern."); }
  };

  if (loading) return <div style={{ padding: 32 }}>Lädt…</div>;

  const inp = (key, type = "text", placeholder = "") => (
    <input type={type} className="form-control" value={werte[key] || ""}
      placeholder={placeholder}
      onChange={e => set(key, e.target.value)} />
  );

  const ta = (key, rows = 3, placeholder = "") => (
    <textarea className="form-control" rows={rows} value={werte[key] || ""}
      placeholder={placeholder}
      onChange={e => set(key, e.target.value)} />
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="breadcrumb">Verwaltung</div>
          <h1 className="page-title">Einstellungen</h1>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "2px solid #eee" }}>
        {TABS.map((t, i) => (
          <button key={i} onClick={() => setTab(i)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            fontSize: 13, fontWeight: tab === i ? 600 : 400,
            color: tab === i ? "var(--red)" : "#555",
            borderBottom: tab === i ? "2px solid var(--red)" : "2px solid transparent",
            marginBottom: -2
          }}>{t}</button>
        ))}
      </div>

      {tab === 0 && (
        <div className="card">
          <div className="card-header"><h3>Organisation</h3></div>
          <div className="card-body">
            <div className="form-row">
              <FormGroup label="Name der Feuerwehr">{inp("ffw_name", "text", "Freiwillige Feuerwehr")}</FormGroup>
              <FormGroup label="Stadtname">{inp("stadt_name", "text", "Stadt Seebad Ueckermünde")}</FormGroup>
            </div>
            <FormGroup label="Slogan">{inp("stadt_slogan", "text", "Der Erholungsort am Stettiner Haff")}</FormGroup>
            <div className="form-row">
              <FormGroup label="Straße">{inp("anschrift_strasse")}</FormGroup>
              <FormGroup label="PLZ">{inp("anschrift_plz")}</FormGroup>
              <FormGroup label="Ort">{inp("anschrift_ort")}</FormGroup>
            </div>
            <div className="form-row">
              <FormGroup label="Telefon">{inp("telefon")}</FormGroup>
              <FormGroup label="Fax">{inp("fax")}</FormGroup>
              <FormGroup label="E-Mail">{inp("email", "email")}</FormGroup>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => save(["ffw_name","stadt_name","stadt_slogan","anschrift_strasse","anschrift_plz","anschrift_ort","telefon","fax","email"])}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {tab === 1 && (
        <div className="card">
          <div className="card-header"><h3>E-Mail / SMTP</h3></div>
          <div className="card-body">
            <div className="form-row">
              <FormGroup label="SMTP-Host">{inp("smtp_host", "text", "smtp.example.com")}</FormGroup>
              <FormGroup label="SMTP-Port">{inp("smtp_port", "number", "587")}</FormGroup>
            </div>
            <div className="form-row">
              <FormGroup label="Benutzername">{inp("smtp_user")}</FormGroup>
              <FormGroup label="Passwort">{inp("smtp_password", "password")}</FormGroup>
            </div>
            <div className="form-row">
              <FormGroup label="Absender-Adresse">{inp("smtp_from", "email")}</FormGroup>
              <FormGroup label="Absender-Name">{inp("smtp_from_name")}</FormGroup>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => save(["smtp_host","smtp_port","smtp_user","smtp_password","smtp_from","smtp_from_name"])}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {tab === 2 && (
        <PdfDesignTab werte={werte} set={set} save={save} api={api} />
      )}

      {tab === 3 && (
        <div className="card">
          <div className="card-header"><h3>Sitzungen</h3></div>
          <div className="card-body">
            <FormGroup label="Standard-Antragsfrist (Tage vor Sitzung)" hint="Wird beim Anlegen automatisch gesetzt.">
              {inp("standard_antragsfrist_tage", "number", "7")}
            </FormGroup>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => save(["standard_antragsfrist_tage"])}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {tab === 4 && (
        <div className="card">
          <div className="card-header"><h3>Protokoll-Versand</h3></div>
          <div className="card-body">
            <FormGroup label="Empfänger-Adressen" hint="Mehrere Adressen kommagetrennt">
              {ta("protokoll_empfaenger", 3, "empfaenger@example.com, weitere@example.com")}
            </FormGroup>
            <FormGroup label="Betreff" hint="{titel} wird durch den Sitzungstitel ersetzt">
              {inp("protokoll_betreff", "text", "Protokoll: {titel}")}
            </FormGroup>
            <FormGroup label="E-Mail-Text">
              {ta("protokoll_text", 4, "Anbei erhalten Sie das Protokoll der Sitzung.")}
            </FormGroup>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => save(["protokoll_empfaenger","protokoll_betreff","protokoll_text"])}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {tab === 6 && <SitzungsortTab api={api} />}
      {tab === 7 && <StandardTopsTab api={api} />}

      {tab === 8 && (
        <div className="card">
          <div className="card-header"><h3>Info-Screen (Projektor 4)</h3></div>
          <div className="card-body">
            <FormGroup label="WLAN-Name (SSID)" hint="Wird auf dem Info-Screen angezeigt. Leer lassen = keine WLAN-Anzeige.">
              {inp("wlan_ssid", "text", "FFW-Gast")}
            </FormGroup>
            <FormGroup label="WLAN-Passwort">
              {inp("wlan_passwort", "text", "")}
            </FormGroup>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => save(["wlan_ssid","wlan_passwort"])}>Speichern</button>
            </div>
          </div>
        </div>
      )}

      {tab === 5 && (
        <div className="card">
          <div className="card-header"><h3>System</h3></div>
          <div className="card-body">
            <FormGroup label="Authentifizierungsmodus" hint="Nach Änderung: Browser-Cache leeren und neu anmelden!">
              <select className="form-control" value={werte.auth_mode || "keycloak"}
                onChange={e => set("auth_mode", e.target.value)}>
                <option value="keycloak">Keycloak / LDAP (extern)</option>
                <option value="internal">Interne Benutzerverwaltung</option>
              </select>
            </FormGroup>
            <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 6, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
              ⚠ Nach dem Umstellen auf "Intern" können sich nur Benutzer anmelden die in der internen Benutzerverwaltung angelegt sind.
            </div>
            <FormGroup label="Keycloak/IPA-Gruppen-Filter" hint="Nur Gruppen unter diesem Pfad werden synchronisiert. Beispiel: /sitzung/ – damit werden alle Gruppen wie /sitzung/ffw-admin und /sitzung/vorstand_ueckermuende übernommen, andere IPA-Gruppen (z.B. /admins, /ipausers) ignoriert. Leer = alle Gruppen.">
              <input type="text" className="form-control"
                value={werte.keycloak_group_filter || ""}
                onChange={e => set("keycloak_group_filter", e.target.value)}
                placeholder="/sitzung/" />
            </FormGroup>
            <FormGroup label="IP & Gerätedaten im Audit-Log speichern" hint="Speichert IP-Adresse, Browser und Gerätetyp bei Anwesenheits-Checkins">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" id="audit_metadaten"
                  checked={werte.audit_metadaten === "true" || werte.audit_metadaten === undefined}
                  onChange={e => set("audit_metadaten", e.target.checked ? "true" : "false")} />
                <label htmlFor="audit_metadaten" style={{ fontSize: 13, cursor: "pointer" }}>Aktiviert</label>
              </div>
            </FormGroup>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={async () => {
                await save(["auth_mode", "audit_metadaten", "keycloak_group_filter"]);
                localStorage.setItem("auth_mode_cache", werte.auth_mode || "keycloak");
                localStorage.removeItem("internal_token");
                localStorage.removeItem("internal_user");
                setTimeout(() => { window.location.href = "/"; }, 500);
              }}>Speichern & neu laden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── PDF DESIGN TAB ──────────────────────────────────────────────────────────
function PdfDesignTab({ werte, set, save, api }) {
  const [activeEditor, setActiveEditor] = useState("einfach");
  const [aktivesTemplate, setAktivesTemplate] = useState("ladung");
  const [gremien, setGremien] = useState([]);
  const [selectedGremiumId, setSelectedGremiumId] = useState(0);
  const editorRef = useRef(null);
  const [ladungTemplate, setLadungTemplate] = useState("");
  const [protokollTemplate, setProtokollTemplate] = useState("");
  const [qrTemplate, setQrTemplate] = useState("");

  useEffect(() => {
    api.getGremien?.().then(setGremien).catch(() => {});
  }, [api]);

  function templateKey(typ, gremiumId) {
    return gremiumId ? `gremium_${gremiumId}_${typ}_template` : `${typ}_template`;
  }

  const loadTemplate = async (name) => {
    try {
      const res = await api.getTemplate(name);
      // Im Gremium-Modus: aktuelles Override (falls da) oder Default als Startpunkt
      if (selectedGremiumId) {
        const overrideKey = templateKey(name, selectedGremiumId);
        const content = werte[overrideKey] || werte[`${name}_template`] || res.content;
        set(overrideKey, content);
        if (editorRef.current) editorRef.current.setValue(content);
        return;
      }
      // Default-Modus: generisch für alle Template-Typen
      const dbKey = `${name}_template`;
      const content = werte[dbKey] || res.content;
      set(dbKey, content);
      // Legacy-States nur für die drei alten Typen synchron halten
      if (name === "ladung") setLadungTemplate(content);
      else if (name === "qr") setQrTemplate(content);
      else if (name === "protokoll") setProtokollTemplate(content);
      if (editorRef.current) editorRef.current.setValue(content);
    } catch(e) { alert("Fehler beim Laden: " + e.message); }
  };

  const resetTemplate = async (name) => {
    if (selectedGremiumId) {
      const gName = gremien.find(g => g.id === selectedGremiumId)?.name || "dieses Gremium";
      if (!window.confirm(`Override für ${gName} löschen?\n\nDanach wird das Standard-Template verwendet.`)) return;
      const key = templateKey(name, selectedGremiumId);
      try {
        await api.deleteEinstellung(key);
        set(key, "");
      } catch (e) { alert("Fehler: " + e.message); }
      return;
    }
    if (!window.confirm("Template auf Standard zurücksetzen?")) return;
    try {
      const res = await api.getTemplate(name);
      const dbKey = `${name}_template`;
      set(dbKey, "");
      save([dbKey]);
      // Legacy-States nur für die drei alten Typen synchron halten
      if (name === "ladung") setLadungTemplate(res.content);
      else if (name === "qr") setQrTemplate(res.content);
      else if (name === "protokoll") setProtokollTemplate(res.content);
    } catch(e) { alert("Fehler"); }
  };

  const currentTemplate = selectedGremiumId
    ? (werte[templateKey(aktivesTemplate, selectedGremiumId)] || "")
    : (werte[`${aktivesTemplate}_template`] || "");
  const setCurrentTemplate = (val) => {
    if (selectedGremiumId) {
      set(templateKey(aktivesTemplate, selectedGremiumId), val);
    } else {
      set(`${aktivesTemplate}_template`, val);
      if (aktivesTemplate === "ladung") setLadungTemplate(val);
      else if (aktivesTemplate === "qr") setQrTemplate(val);
      else if (aktivesTemplate === "protokoll") setProtokollTemplate(val);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button onClick={() => setActiveEditor("einfach")}
          className={activeEditor === "einfach" ? "btn btn-primary" : "btn btn-secondary"}>
          🎨 Einfach
        </button>
        <button onClick={() => setActiveEditor("erweitert")}
          className={activeEditor === "erweitert" ? "btn btn-primary" : "btn btn-secondary"}>
          &lt;/&gt; Erweitert (HTML)
        </button>
      </div>

      {activeEditor === "einfach" && (
        <div className="grid-2">
          <div className="card">
            <div className="card-header"><h3>Design-Einstellungen</h3></div>
            <div className="card-body">
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Primärfarbe</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={werte.pdf_primaerfarbe || "#cc0000"}
                    onChange={e => set("pdf_primaerfarbe", e.target.value)}
                    style={{ width: 50, height: 36, padding: 2, border: "1px solid #ddd", borderRadius: 4, cursor: "pointer" }} />
                  <input type="text" className="form-control" value={werte.pdf_primaerfarbe || "#cc0000"}
                    onChange={e => set("pdf_primaerfarbe", e.target.value)} style={{ width: 100 }} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Akzentfarbe</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={werte.pdf_akzentfarbe || "#1a1a1a"}
                    onChange={e => set("pdf_akzentfarbe", e.target.value)}
                    style={{ width: 50, height: 36, padding: 2, border: "1px solid #ddd", borderRadius: 4, cursor: "pointer" }} />
                  <input type="text" className="form-control" value={werte.pdf_akzentfarbe || "#1a1a1a"}
                    onChange={e => set("pdf_akzentfarbe", e.target.value)} style={{ width: 100 }} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Schriftart</div>
                <select className="form-control" value={werte.pdf_schriftart || "Arial"}
                  onChange={e => set("pdf_schriftart", e.target.value)}>
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Schriftgröße (pt)</div>
                <input type="number" className="form-control" min="8" max="14"
                  value={werte.pdf_schriftgroesse || "10"}
                  onChange={e => set("pdf_schriftgroesse", e.target.value)} style={{ width: 80 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-primary"
                  onClick={() => save(["pdf_primaerfarbe","pdf_akzentfarbe","pdf_schriftart","pdf_schriftgroesse"])}>
                  Speichern
                </button>
              </div>
            </div>
          </div>

          <div className="card" style={{ position: "sticky", top: 20, height: "fit-content" }}>
            <div className="card-header"><h3>Vorschau</h3></div>
            <div className="card-body" style={{ fontFamily: werte.pdf_schriftart || "Arial", fontSize: "10pt" }}>
              <div style={{ borderBottom: `3px solid ${werte.pdf_primaerfarbe || "#cc0000"}`, paddingBottom: 6, marginBottom: 8 }}>
                <div style={{ fontSize: "7pt", color: "#555", textAlign: "right" }}>Der Erholungsort am Stettiner Haff</div>
                <div style={{ fontSize: "16pt", fontWeight: 900, color: werte.pdf_akzentfarbe || "#1a1a1a",
                              borderBottom: `3px solid ${werte.pdf_primaerfarbe || "#cc0000"}`, paddingBottom: 2 }}>
                  STADT SEEBAD UECKERMÜNDE
                </div>
                <div style={{ fontSize: "8pt", color: "#555", textAlign: "right" }}>Freiwillige Feuerwehr</div>
              </div>
              <div style={{ fontSize: "8pt", borderBottom: "1px solid #000", marginBottom: 12, paddingBottom: 2 }}>
                Freiwillige Feuerwehr ● Ueckerstraße 132a ● 17373 Ueckermünde
              </div>
              <div style={{ fontWeight: "bold", marginBottom: 2 }}>Max Mustermann</div>
              <div style={{ marginBottom: 12, fontSize: "9pt" }}>Musterstraße 1<br/>17373 Ueckermünde</div>
              <div style={{ marginTop: 12 }}>
                <div style={{ width: 20, height: 2, background: "#000", marginBottom: 4 }}></div>
                <div style={{ fontWeight: "bold" }}>Betreff: Ladung zur Sitzung</div>
                <div style={{ paddingLeft: 16 }}>hier: Vorstandssitzung 2026</div>
              </div>
              <div style={{ marginTop: 12, fontSize: "9pt" }}>
                Sehr geehrte Damen und Herren,<br/><br/>
                hiermit laden wir Sie herzlich zur Sitzung ein.
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: "bold", marginBottom: 4 }}>Tagesordnung:</div>
                <div style={{ borderBottom: "1px solid #eee", padding: "3px 0" }}><strong>1.</strong> Eröffnung</div>
                <div style={{ borderBottom: "1px solid #eee", padding: "3px 0" }}>
                  <strong>2.</strong> Jahresbericht{" "}
                  <span style={{ fontSize: "8pt", color: werte.pdf_primaerfarbe || "#cc0000",
                                border: `1px solid ${werte.pdf_primaerfarbe || "#cc0000"}`, padding: "1px 3px" }}>BESCHLUSS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeEditor === "erweitert" && (
        <div>
          <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #eee" }}>
            {["ladung", "protokoll", "qr", "vorlage", "beschlussvorlage", "beschluss"].map(name => (
              <button key={name} onClick={() => setAktivesTemplate(name)}
                style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: aktivesTemplate === name ? 600 : 400,
                  color: aktivesTemplate === name ? "var(--red)" : "#555",
                  borderBottom: aktivesTemplate === name ? "2px solid var(--red)" : "2px solid transparent",
                  marginBottom: -1 }}>
                {name === "ladung" ? "📄 Ladung" : name === "qr" ? "📱 QR-Code" : name === "protokoll" ? "📋 Protokoll" : name === "vorlage" ? "📑 Vorlage" : name === "beschlussvorlage" ? "📎 Beschlussvorlage (alt)" : "⚖️ Beschluss"}
              </button>
            ))}
          </div>


          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", background: "#f5f5f5", borderRadius: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>Gilt für:</span>
            <select
              value={selectedGremiumId}
              onChange={e => setSelectedGremiumId(parseInt(e.target.value, 10))}
              style={{ padding: "4px 8px", fontSize: 13, borderRadius: 4, border: "1px solid #ccc" }}
            >
              <option value={0}>Standard (alle Gremien)</option>
              {gremien.map(g => {
                const hasOverride = !!werte[templateKey(aktivesTemplate, g.id)];
                return (
                  <option key={g.id} value={g.id}>
                    {g.name}{hasOverride ? " ●" : ""}
                  </option>
                );
              })}
            </select>
            {selectedGremiumId !== 0 && (
              <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                {werte[templateKey(aktivesTemplate, selectedGremiumId)]
                  ? "● Override aktiv"
                  : "Kein Override – Standard wird verwendet"}
              </span>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="card">
              <div className="card-header">
                <h3>{aktivesTemplate === "ladung" ? "Ladungs-Template" : aktivesTemplate === "qr" ? "QR-Code-Template" : aktivesTemplate === "protokoll" ? "Protokoll-Template" : aktivesTemplate === "vorlage" ? "Vorlage-Template (Beschlussvorlagen-PDF)" : aktivesTemplate === "beschlussvorlage" ? "Beschlussvorlage-Template (alt)" : "Beschluss-Template"}</h3>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => loadTemplate(aktivesTemplate)}>⬇ Laden</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => resetTemplate(aktivesTemplate)}>↩ Standard</button>
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    const key = templateKey(aktivesTemplate, selectedGremiumId);
                    const val = editorRef.current ? editorRef.current.getValue() : currentTemplate;
                    if (!selectedGremiumId) {
                      if (aktivesTemplate === "ladung") setLadungTemplate(val);
                      else if (aktivesTemplate === "qr") setQrTemplate(val);
                      else if (aktivesTemplate === "protokoll") setProtokollTemplate(val);
                    }
                    set(key, val);
                    save([key]);
                  }}>Speichern</button>
                </div>
              </div>
              <div style={{ padding: 0 }}>
                <Editor
                  height="500px"
                  defaultLanguage="html"
                  value={currentTemplate}
                  onChange={val => setCurrentTemplate(val || "")}
                  onMount={(editor) => { editorRef.current = editor; }}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 12,
                    wordWrap: "on",
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    lineNumbers: "on",
                    folding: true,
                  }}
                  theme="vs-light"
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3>Vorschau (HTML)</h3>
                <div style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>Jinja2-Tags werden als Text angezeigt</div>
              </div>
              <div style={{ padding: 0, overflow: "hidden" }}>
                <iframe
                  srcDoc={currentTemplate ? currentTemplate + `<script>
                    document.addEventListener('click', function(e) {
                      var el = e.target;
                      var tag = el.tagName.toLowerCase();
                      var cls = el.className ? '.' + el.className.split(' ')[0] : '';
                      var id = el.id ? '#' + el.id : '';
                      window.parent.postMessage({type:'elementClick', search: tag + id + cls, outerHTML: el.outerHTML.slice(0,100)}, '*');
                    });
                  </script>` : "<div style='padding:20px;color:#999;font-family:Arial'>Template laden...</div>"}
                  style={{ width: "100%", height: 500, border: "none", background: "white" }}
                  title="Vorschau"
                  sandbox="allow-same-origin allow-scripts"
                  onLoad={e => {
                    window.onmessage = (msg) => {
                      if (msg.data.type === "elementClick" && editorRef.current) {
                        const search = msg.data.outerHTML.slice(0, 50);
                        const model = editorRef.current.getModel();
                        const content = model.getValue();
                        const idx = content.indexOf(search.slice(0, 30));
                        if (idx >= 0) {
                          const pos = model.getPositionAt(idx);
                          editorRef.current.revealLineInCenter(pos.lineNumber);
                          editorRef.current.setPosition(pos);
                          editorRef.current.focus();
                          editorRef.current.deltaDecorations([], [{
                            range: new (window.monaco?.Range || class{})(pos.lineNumber, 1, pos.lineNumber, 999),
                            options: { isWholeLine: true, className: "highlighted-line", inlineClassName: "highlighted-line" }
                          }]);
                        }
                      }
                    };
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, background: "#f5f5f5", padding: "10px 14px", borderRadius: 6, fontSize: 12 }}>
            <strong>Verfügbare Variablen:</strong><br/>
            <code>{"{{ sitzung.titel }}"}</code> · <code>{"{{ sitzung.datum | de_datum }}"}</code> ·
            <code>{"{{ sitzung.aktenzeichen }}"}</code> · <code>{"{{ sitzung.gremium.name }}"}</code> ·
            <code>{"{% for top in sitzung.tops %} ... {% endfor %}"}</code> ·
            <code>{"{{ member.display_name }}"}</code> · <code>{"{{ member.anschrift }}"}</code>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SITZUNGSORTE TAB ────────────────────────────────────────────────────────
function SitzungsortTab({ api }) {
  const [orte, setOrte] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editOrt, setEditOrt] = useState(null);
  const [form, setForm] = useState({ name: "", strasse: "", hausnummer: "", plz: "", ort: "", raum: "" });
  const [success, setSuccess] = useState("");

  const load = () => api.getSitzungsorte().then(setOrte).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editOrt) await api.updateSitzungsort(editOrt.id, form);
      else await api.createSitzungsort(form);
      setShowForm(false); setEditOrt(null);
      setForm({ name: "", strasse: "", hausnummer: "", plz: "", ort: "", raum: "" });
      setSuccess("Gespeichert!"); setTimeout(() => setSuccess(""), 3000);
      load();
    } catch { alert("Fehler beim Speichern"); }
  };

  const del = async (o) => {
    if (!window.confirm(`${o.name} wirklich löschen?`)) return;
    await api.deleteSitzungsort(o.id);
    load();
  };

  const openEdit = (o) => {
    setEditOrt(o);
    setForm({ name: o.name, strasse: o.strasse || "", hausnummer: o.hausnummer || "", plz: o.plz || "", ort: o.ort || "", raum: o.raum || "" });
    setShowForm(true);
  };

  return (
    <div>
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      <div className="card">
        <div className="card-header">
          <h3>Sitzungsorte</h3>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }}
            onClick={() => { setEditOrt(null); setForm({ name: "", strasse: "", hausnummer: "", plz: "", ort: "", raum: "" }); setShowForm(true); }}>
            + Ort hinzufügen
          </button>
        </div>
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Adresse</th><th>Raum</th><th></th></tr>
          </thead>
          <tbody>
            {orte.map(o => (
              <tr key={o.id}>
                <td><strong>{o.name}</strong></td>
                <td style={{ fontSize: 12 }}>{o.strasse} {o.hausnummer}, {o.plz} {o.ort}</td>
                <td style={{ fontSize: 12 }}>{o.raum || "—"}</td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Bearbeiten" aria-label="Bearbeiten" onClick={() => openEdit(o)}>✎</button>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Löschen" aria-label="Löschen" onClick={() => del(o)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
            {orte.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "#999", padding: 20 }}>Noch keine Sitzungsorte definiert</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 8, padding: 24, width: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ marginBottom: 16 }}>{editOrt ? "Ort bearbeiten" : "Neuer Sitzungsort"}</h3>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Name / Liegenschaft *</div>
              <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Gerätehaus Ueckermünde" />
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Straße</div>
                <input className="form-control" value={form.strasse} onChange={e => setForm(f => ({ ...f, strasse: e.target.value }))} />
              </div>
              <div style={{ width: 80 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Nr.</div>
                <input className="form-control" value={form.hausnummer} onChange={e => setForm(f => ({ ...f, hausnummer: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 100 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>PLZ</div>
                <input className="form-control" value={form.plz} onChange={e => setForm(f => ({ ...f, plz: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Ort</div>
                <input className="form-control" value={form.ort} onChange={e => setForm(f => ({ ...f, ort: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Raum (optional)</div>
              <input className="form-control" value={form.raum} onChange={e => setForm(f => ({ ...f, raum: e.target.value }))} placeholder="z. B. Schulungsraum, EG" />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditOrt(null); }}>Abbrechen</button>
              <button className="btn btn-primary" onClick={save}>Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
